import React from 'react';
import dayjs from 'dayjs';
import Checkbox from '@mui/material/Checkbox';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import TextAreaCustom from '../../../common/TextAreaCustom';
import FormControlLabel from '@mui/material/FormControlLabel';
import { FieldErrors } from '../../../domain/helpers/Errors';
import { IncidentInterface } from '../../../models/Incident';
import { BootstrapInput } from '../../../common/GlobalFunctions';
import { useLanguageStore } from '../../../store/useLanguageStore';
import { useDarkModeStore } from '../../../store/useDarkModeStore';

export type IncidentTypeInputs = 'text' | 'multitext' | 'select' | 'checkbox' | 'date' | 'date-time';

interface CustomizedInputsStyledProps<T> {
    view: boolean;
    label: string;
    name: string;
    errors: FieldErrors;
    type: IncidentTypeInputs;
    form: IncidentInterface;
    setForm: React.Dispatch<React.SetStateAction<IncidentInterface>>;
}

const CustomizedInputsStyled = <T,>({ label, type, name, form, errors, view, setForm }: CustomizedInputsStyledProps<T>) => {

    const { language } = useLanguageStore();
    const { darkMode } = useDarkModeStore();

    const handleText = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        delete errors?.[name];
        setForm({ ...form, [name]: value });
    }

    const handleDate = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        delete errors?.[name];
        const res = value ? dayjs(value).format('YYYY-MM-DD') : undefined;
        setForm({ ...form, [name]: res });
    }

    const handleDateTime = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        delete errors?.[name];
        const res = value ? dayjs(value).format('YYYY-MM-DDTHH:mm') : undefined;
        setForm({ ...form, [name]: res });
    }

    const renderInput = () => {
        switch (type) {
            case 'text':
                return (
                    <BootstrapInput
                        darkmode={darkMode ? darkMode.toString() : 'false'}
                        value={form?.[name] ?? ''}
                        id={name}
                        name={name}
                        onChange={handleText}
                        sx={errors?.[name] ? { border: '1px solid #DC3545', borderRadius: '4px' } : {}}
                        readOnly={view}
                    />
                );
            case 'multitext':
                return (
                    <TextAreaCustom
                        errors={errors}
                        form={form}
                        name={name}
                        setForm={setForm}
                        view={view}
                        className='mt-4'
                    />
                );
            case 'date':
                if (name === 'dateTime') return (
                    <BootstrapInput
                        darkmode={darkMode ? darkMode.toString() : 'false'}
                        value={form?.[name] ? dayjs(form[name]).format('YYYY-MM-DDTHH:mm') : ''}
                        id={name}
                        name={name}
                        type="datetime-local"
                        sx={errors?.[name] ? { border: '1px solid #DC3545', borderRadius: '4px' } : {}}
                        onChange={handleDateTime}
                        readOnly={view}
                    />)
                else return (
                    <BootstrapInput
                        darkmode={darkMode ? darkMode.toString() : 'false'}
                        value={form?.[name] ? dayjs(form[name]).format('YYYY-MM-DD') : form?.[name] ?? ''}
                        id={name}
                        name={name}
                        type="date"
                        sx={errors?.[name] ? { border: '1px solid #DC3545', borderRadius: '4px' } : {}}
                        onChange={handleDate}
                        readOnly={view}
                    />
                );
            case 'checkbox':
                return (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={form[name] as boolean}
                                id={name}
                                name={name}
                                onChange={handleText}
                                sx={errors?.[name] ? { border: '1px solid #DC3545', borderRadius: '4px' } : {}}
                            />
                        }
                        label={language[label]}
                    />
                );
            default:
                return null;
        }
    }

    return (
        <FormControl variant="standard" className={`${(type === 'multitext' || name === 'dateTime') && 'modalMergeTwoRows'}`} >
            <InputLabel shrink htmlFor={name} sx={errors?.[name] ? { color: '#DC3545' } : { color: darkMode ? 'white' : 'rgba(0, 0, 0, 0.6)' }}>
                {language[label]}
            </InputLabel>
            {renderInput()}
            {errors?.[name] && <p className='text-danger mb-0' style={{ fontSize: '12px' }}>{errors?.[name]}</p>}
        </FormControl>
    );
}

export default CustomizedInputsStyled;
